import { useEffect, useState } from "react";
import axios from "axios";
import { baseURL } from "../Constants";
import { TeamTier, TeamSubscription } from "../models/TeamTier";

const url = `${baseURL}/TeamTier`;

const CACHE_KEY = 'team_tiers_cache';
// 5 minutes, matching the server's own tier refresh cadence. Shorter than the donator ledger's 30
// because a tier is bought and expected to work immediately: a longer TTL means a team that just
// paid keeps seeing "locked" in every already-open browser until the cache ages out.
const CACHE_DURATION_MS = 5 * 60 * 1000;

interface CachedTeamTiers {
    data: TeamTier[];
    timestamp: number;
}

/**
 * Every active team tier with the players it covers. One small payload drives all the team-tier
 * gates and the badge, the same way getAllDonators() already drives the personal ones.
 */
export const getTeamTiers = async (force = false): Promise<TeamTier[]> => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached && !force) {
            const { data, timestamp }: CachedTeamTiers = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION_MS) {
                return data;
            }
        }
    } catch (e) {
        console.warn('Failed to parse team tiers cache:', e);
    }

    try {
        const response = await axios.get<TeamTier[]>(url);
        const tiers = response.data ?? [];

        localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: tiers,
            timestamp: Date.now()
        }));

        return tiers;
    } catch (error) {
        console.error('Error fetching team tiers:', error);

        // Stale cache beats no perks: a failed fetch must not lock a paying team out.
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { data }: CachedTeamTiers = JSON.parse(cached);
                return data;
            }
        } catch (e) {
            // Ignore
        }

        return [];
    }
};

export const clearTeamTiersCache = (): void => {
    localStorage.removeItem(CACHE_KEY);
    // Also drop the shared page-wide copy below, or useTeamTiers() consumers keep serving the
    // pre-edit list until a reload — which on the admin page is exactly the moment it changed.
    moduleTiers = null;
    tiersPromise = null;
};

/** The tier covering this player, or null. */
export const getTeamTierForSteamId = (steamId: string | undefined, tiers: TeamTier[]): TeamTier | null => {
    if (!steamId) return null;
    return tiers.find(t => t.coveredSteamIds.includes(steamId)) ?? null;
};

export const isTeamTierCovered = (steamId: string | undefined, tiers: TeamTier[]): boolean =>
    getTeamTierForSteamId(steamId, tiers) !== null;

/** Does this team hold a tier? Used for the badge and the LFP sort. */
export const isTeamTierTeam = (teamId: string | undefined | null, tiers: TeamTier[]): boolean => {
    if (!teamId) return false;
    return tiers.some(t => t.teamId === teamId);
};

// ---------------------------------------------------------------------------
// Shared page-wide tier state
//
// Mirrors BadgeService: localStorage is read synchronously at module load, so a warm cache costs
// no request and the badge never flickers in. Only a cold cache needs the single background fetch,
// which every consumer shares — a page full of TeamLogos costs one request.
// ---------------------------------------------------------------------------
let moduleTiers: TeamTier[] | null = null;

try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
        const { data, timestamp }: CachedTeamTiers = JSON.parse(raw);
        if (Date.now() - timestamp < CACHE_DURATION_MS) {
            moduleTiers = data;
        }
    }
} catch {
    // Ignore parse errors
}

let tiersPromise: Promise<TeamTier[]> | null = null;
if (!moduleTiers) {
    tiersPromise = getTeamTiers().then((t) => {
        moduleTiers = t;
        return t;
    });
}

/**
 * Team tiers for the current page. Resolves synchronously on a warm cache, otherwise returns an
 * empty list until the one shared background fetch lands.
 */
export const useTeamTiers = (): TeamTier[] => {
    const [tiers, setTiers] = useState<TeamTier[] | null>(moduleTiers);

    useEffect(() => {
        if (!tiers && tiersPromise) {
            tiersPromise.then(setTiers);
        }
    }, [tiers]);

    return tiers ?? [];
};

// ---- Admin management (requires an admin JWT in localStorage) ----

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export interface TeamTierPayload {
    teamId: string;
    amount: number;
    purchasedBySteamId?: string | null;
    note?: string | null;
}

/** Fresh (non-cached) list for the admin page — includes deactivated rows. */
export const getTeamTiersForAdmin = async (): Promise<TeamSubscription[]> => {
    const response = await axios.get<TeamSubscription[]>(`${url}/admin`, { headers: authHeader() });
    return response.data;
};

export const addTeamTier = async (
    payload: TeamTierPayload
): Promise<{ subscription: TeamSubscription; coveredPlayers: number }> => {
    const response = await axios.post(`${url}/admin`, payload, { headers: authHeader() });
    clearTeamTiersCache();
    return response.data;
};

export const updateTeamTier = async (
    id: number,
    payload: TeamTierPayload & { active: boolean }
): Promise<void> => {
    await axios.put(`${url}/admin/${id}`, payload, { headers: authHeader() });
    clearTeamTiersCache();
};

export const deleteTeamTier = async (id: number): Promise<void> => {
    await axios.delete(`${url}/admin/${id}`, { headers: authHeader() });
    clearTeamTiersCache();
};
